import React, { useState, useEffect, useCallback, useMemo } from "react";
import api from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { useAuth } from "@/context/AuthContext";
import { formatIDR, formatDateTime } from "@/lib/format";
import { PageHeader, EmptyState, Field } from "@/components/ui-helpers";
import { PricingBreakdown, breakdownTextLines, discountLabel } from "@/components/PricingBreakdown";
import { PricingLinesEditor, linesFromOrder, stripLineKeys, validateLines, estimateTotal } from "@/components/PricingLinesEditor";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SearchableSelect } from "@/components/SearchableSelect";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Download, MessageCircle, Loader2, Users, Weight, SlidersHorizontal, Lock, Unlock, Pencil, History, StickyNote, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const STATUS_STYLE = {
  ACTIVE: "border-primary text-primary",
  PARTIAL: "border-[hsl(var(--warning))] text-[hsl(var(--warning))]",
  PAID: "border-[hsl(var(--success))] text-[hsl(var(--success))]",
  FINALIZED: "border-muted-foreground text-muted-foreground",
};
const STATUS_LABEL = { ACTIVE: "Aktif", PARTIAL: "Sebagian", PAID: "Lunas", FINALIZED: "Final" };
const ACTION_LABEL = {
  edit_adjustments: "Penyesuaian biaya/diskon (lama)",
  edit_lines: "Ubah biaya/diskon order",
  edit_order: "Ubah order",
  edit_line: "Ubah harga item",
  finalize: "Difinalisasi (dikunci)",
  unlock: "Buka kunci",
  migrate_lines: "Migrasi biaya invoice ke order",
};

export default function Invoices() {
  const { trips, selectedTripId } = useApp();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [tripId, setTripId] = useState(selectedTripId && selectedTripId !== "all" ? selectedTripId : "");
  const [customers, setCustomers] = useState([]);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [activeCustomer, setActiveCustomer] = useState(null);
  const [invoice, setInvoice] = useState(null);
  const [loadingInvoice, setLoadingInvoice] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [lineEdit, setLineEdit] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  // per-order fee/discount editor
  const [linesOpen, setLinesOpen] = useState(false);
  const [linesOrderId, setLinesOrderId] = useState("");
  const [feeLines, setFeeLines] = useState([]);
  const [discountLines, setDiscountLines] = useState([]);
  // invoice notes
  const [notesOpen, setNotesOpen] = useState(false);
  const [notesDraft, setNotesDraft] = useState("");

  useEffect(() => {
    if (!tripId && trips.length) {
      setTripId(selectedTripId && selectedTripId !== "all" ? selectedTripId : trips[0].id);
    }
  }, [trips]); // eslint-disable-line

  const loadCustomers = useCallback(async (keepActive = false) => {
    if (!tripId) { setCustomers([]); return; }
    setLoadingCustomers(true);
    if (!keepActive) { setActiveCustomer(null); setInvoice(null); }
    try {
      const { data } = await api.get("/invoices/customers", { params: { trip_id: tripId } });
      setCustomers(data);
    } catch { toast.error("Gagal memuat daftar pelanggan"); setCustomers([]); }
    finally { setLoadingCustomers(false); }
  }, [tripId]);

  useEffect(() => { loadCustomers(); }, [loadCustomers]);

  const openInvoice = async (c) => {
    setActiveCustomer(c.customer_id);
    setLoadingInvoice(true); setInvoice(null);
    try {
      const { data } = await api.get(`/invoices/${tripId}/${c.customer_id}`);
      setInvoice(data);
    } catch { toast.error("Gagal memuat invoice"); }
    finally { setLoadingInvoice(false); }
  };

  const multi = (invoice?.orders || []).length > 1;
  const orderById = useMemo(() => Object.fromEntries((invoice?.orders || []).map((o) => [o.order_id, o])), [invoice]);

  // ---- per-order lines editor -------------------------------------------
  const openLines = (orderId) => {
    if (!invoice) return;
    const oid = orderId || invoice.default_order_id || invoice.orders[0]?.order_id;
    const o = orderById[oid];
    if (!o) return toast.error("Order tidak ditemukan");
    const ln = linesFromOrder(o);
    setLinesOrderId(oid); setFeeLines(ln.feeLines); setDiscountLines(ln.discountLines);
    setLinesOpen(true);
  };
  const switchLinesOrder = (oid) => {
    const o = orderById[oid];
    if (!o) return;
    const ln = linesFromOrder(o);
    setLinesOrderId(oid); setFeeLines(ln.feeLines); setDiscountLines(ln.discountLines);
  };

  const saveLines = async (unlock = false, reason = "") => {
    const err = validateLines(feeLines, discountLines);
    if (err) return toast.error(err);
    const o = orderById[linesOrderId];
    if (!o) return;
    if (!unlock && invoice.status === "PAID") {
      const est = estimateTotal(o.items_subtotal, feeLines, discountLines);
      if (est > o.order_total) {
        if (!window.confirm(`Invoice ini sudah LUNAS. Perubahan ini menambah tagihan sekitar ${formatIDR(est - o.order_total)} sehingga pelanggan akan punya sisa tagihan baru. Lanjutkan?`)) return;
      }
    }
    setSaving(true);
    try {
      const { data } = await api.put(`/invoices/${tripId}/${activeCustomer}/order-lines`, {
        order_id: linesOrderId, fee_lines: stripLineKeys(feeLines), discount_lines: stripLineKeys(discountLines), unlock, reason,
      });
      setInvoice(data); setLinesOpen(false);
      toast.success(`Biaya & diskon ${o.order_number} disimpan`, { description: `Total invoice ${formatIDR(data.totals.grand_total)}` });
      loadCustomers(true);
    } catch (e) {
      const d = e.response?.data?.detail;
      if (d?.code === "INVOICE_LOCKED") {
        if (!isAdmin) { toast.error("Invoice sudah final — hanya admin yang dapat membuka kunci."); return; }
        const r2 = window.prompt("Invoice sudah FINAL. Masukkan alasan untuk membuka kunci & menyimpan perubahan:");
        if (r2 && r2.trim()) { setSaving(false); return saveLines(true, r2.trim()); }
      } else toast.error(typeof d === "string" ? d : "Gagal menyimpan");
    } finally { setSaving(false); }
  };

  const saveNotes = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/invoices/${tripId}/${activeCustomer}/notes`, { notes: notesDraft });
      setInvoice(data); setNotesOpen(false); toast.success("Catatan invoice disimpan");
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal menyimpan catatan"); }
    finally { setSaving(false); }
  };

  const finalizeInvoice = async () => {
    if (!window.confirm("Kunci invoice ini sebagai final? Harga order tidak dapat diubah sampai invoice dibuka kembali oleh admin.")) return;
    try { const { data } = await api.post(`/invoices/${tripId}/${activeCustomer}/finalize`); setInvoice(data); toast.success("Invoice difinalisasi"); loadCustomers(true); }
    catch { toast.error("Gagal memfinalisasi"); }
  };

  const unlockInvoice = async () => {
    const reason = window.prompt("Alasan membuka kunci invoice:");
    if (!reason || !reason.trim()) return;
    try { const { data } = await api.post(`/invoices/${tripId}/${activeCustomer}/unlock`, { reason: reason.trim() }); setInvoice(data); toast.success("Invoice dibuka"); loadCustomers(true); }
    catch (e) { toast.error(e.response?.data?.detail || "Gagal membuka kunci"); }
  };

  const saveLine = async (unlock = false, reason = "") => {
    if (!lineEdit) return;
    setSaving(true);
    try {
      const { data } = await api.put(`/invoices/${tripId}/${activeCustomer}/line`, {
        order_id: lineEdit.order_id, item_index: lineEdit.item_index, unit_price: String(lineEdit.unit_price || "0"), unlock, reason,
      });
      setInvoice(data); setLineEdit(null); toast.success("Harga item diperbarui"); loadCustomers(true);
    } catch (e) {
      const d = e.response?.data?.detail;
      if (d?.code === "INVOICE_LOCKED") {
        const r2 = isAdmin ? window.prompt("Invoice terkunci. Alasan membuka & mengubah harga:") : null;
        if (r2 && r2.trim()) { setSaving(false); return saveLine(true, r2.trim()); }
        if (!isAdmin) toast.error("Invoice terkunci — hanya admin.");
      } else toast.error(typeof d === "string" ? d : "Gagal menyimpan harga");
    } finally { setSaving(false); }
  };

  const downloadPdf = async () => {
    if (!invoice) return;
    setDownloading(true);
    try {
      const res = await api.get(`/invoices/${tripId}/${activeCustomer}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `${invoice.invoice_number}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Gagal mengunduh PDF"); }
    finally { setDownloading(false); }
  };

  const withOrder = (name, l) => (multi ? `${name} (${l.order_number})` : name);

  const shareWhatsApp = () => {
    if (!invoice) return;
    const t = invoice.totals;
    const lines = [];
    lines.push(`*${invoice.business.business_name}*`);
    lines.push(`Invoice ${invoice.invoice_number} · ${invoice.date}`);
    lines.push(`Trip: ${invoice.trip.name}`);
    lines.push(`Kepada: ${invoice.customer.name}`);
    lines.push("");
    invoice.orders.forEach((o) => {
      lines.push(`_${o.order_number} (${o.payment_status})_`);
      o.items.forEach((it) => {
        lines.push(`• ${it.name}${it.variant ? ` (${it.variant})` : ""} x${it.quantity} — ${formatIDR(it.line_total)}`);
      });
    });
    lines.push("");
    lines.push(...breakdownTextLines({
      subtotal: t.subtotal,
      feeLines: (invoice.fee_lines || []).map((f) => ({ ...f, name: withOrder(f.name || "Biaya", f) })),
      discountLines: (invoice.discount_lines || []).map((d) => ({ ...d, name: withOrder(d.name || "Diskon", d) })),
      total: t.grand_total, paid: t.paid, balance: t.balance, bold: true, alwaysSubtotal: true,
    }));
    lines.push(`Estimasi berat: ${t.total_weight_kg} kg`);
    lines.push("");
    lines.push("Terima kasih sudah memesan 🙏");
    const num = (invoice.customer.whatsapp || "").replace(/[^0-9]/g, "");
    const base = num ? `https://wa.me/${num}` : "https://wa.me/";
    window.open(`${base}?text=${encodeURIComponent(lines.join("\n"))}`, "_blank");
  };

  return (
    <div className="space-y-5">
      <PageHeader title="Invoice" subtitle="Tampilan langsung dari semua order pelanggan per trip — angka selalu sama dengan order">
        <div className="w-full sm:w-64">
          <SearchableSelect
            value={tripId}
            onChange={setTripId}
            options={trips.map((t) => ({ value: t.id, label: t.name, keywords: [t.name, t.code] }))}
            placeholder="Pilih trip"
            searchPlaceholder="Cari trip..."
            emptyText="Trip tidak ditemukan"
            testId="invoices-trip-select"
          />
        </div>
      </PageHeader>

      <div className="grid lg:grid-cols-[320px_1fr] gap-5">
        {/* Customer list */}
        <div className="rounded-2xl border border-border bg-card p-3">
          <div className="flex items-center gap-2 px-2 py-1.5 text-sm font-medium text-muted-foreground">
            <Users className="h-4 w-4" />Pelanggan dengan order
          </div>
          {loadingCustomers ? (
            <div className="space-y-2 mt-2">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-14 w-full rounded-xl" />)}</div>
          ) : customers.length === 0 ? (
            <EmptyState title="Belum ada order" subtitle="Pilih trip lain atau buat order dulu." />
          ) : (
            <div className="space-y-1.5 mt-1 max-h-[70vh] overflow-y-auto pr-1" data-testid="invoices-customer-list">
              {customers.map((c) => (
                <button key={c.customer_id} onClick={() => openInvoice(c)} data-testid="invoices-customer-item"
                  className={`w-full text-left rounded-xl border p-3 transition-colors ${activeCustomer === c.customer_id ? "border-primary bg-primary/5" : "border-border hover:bg-secondary/50"}`}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-medium text-sm truncate">{c.name || "Tanpa nama"}</span>
                    {c.balance > 0
                      ? <Badge variant="outline" className="text-[10px] border-[hsl(var(--warning))] text-[hsl(var(--warning))]">Sisa {formatIDR(c.balance)}</Badge>
                      : <Badge variant="outline" className="text-[10px] border-primary text-primary">Lunas</Badge>}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">{c.orders} order · {formatIDR(c.total)}</p>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Invoice preview */}
        <div className="rounded-2xl border border-border bg-card p-5 min-h-[300px]">
          {loadingInvoice ? (
            <div className="space-y-3"><Skeleton className="h-8 w-52" /><Skeleton className="h-4 w-40" /><Skeleton className="h-40 w-full" /></div>
          ) : !invoice ? (
            <EmptyState title="Pilih pelanggan" subtitle="Pilih pelanggan di kiri untuk melihat invoice." />
          ) : (
            <div data-testid="invoice-preview" className="space-y-5">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h2 className="font-heading text-xl font-semibold text-primary">{invoice.business.business_name}</h2>
                  <p className="text-sm text-muted-foreground">Invoice {invoice.invoice_number} · {invoice.date}</p>
                </div>
                <div className="flex flex-wrap gap-2 items-center">
                  <Badge variant="outline" className={`text-[11px] ${STATUS_STYLE[invoice.status] || ""}`} data-testid="invoice-status">
                    {invoice.locked && <Lock className="h-3 w-3 mr-1" />}{STATUS_LABEL[invoice.status] || invoice.status}
                  </Badge>
                  {invoice.history?.length > 0 && <Button variant="ghost" size="sm" onClick={() => setShowHistory(true)} data-testid="invoice-history-btn"><History className="h-4 w-4 mr-1.5" />Riwayat</Button>}
                  <Button variant="outline" onClick={() => openLines(null)} data-testid="invoice-edit-btn"><SlidersHorizontal className="h-4 w-4 mr-1.5" />Biaya &amp; Diskon</Button>
                  <Button variant="outline" onClick={() => { setNotesDraft(invoice.notes || ""); setNotesOpen(true); }} data-testid="invoice-notes-btn"><StickyNote className="h-4 w-4 mr-1.5" />Catatan</Button>
                  {invoice.status === "FINALIZED" ? (
                    isAdmin && <Button variant="outline" onClick={unlockInvoice} data-testid="invoice-unlock-btn"><Unlock className="h-4 w-4 mr-1.5" />Buka Kunci</Button>
                  ) : (
                    isAdmin && <Button variant="outline" onClick={finalizeInvoice} data-testid="invoice-finalize-btn"><Lock className="h-4 w-4 mr-1.5" />Finalkan</Button>
                  )}
                  <Button variant="outline" onClick={shareWhatsApp} data-testid="invoice-share-wa"><MessageCircle className="h-4 w-4 mr-1.5" />WhatsApp</Button>
                  <Button onClick={downloadPdf} disabled={downloading} data-testid="invoice-download-pdf">
                    {downloading ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Download className="h-4 w-4 mr-1.5" />}PDF
                  </Button>
                </div>
              </div>

              {invoice.locked && (
                <div className="rounded-xl border border-border bg-secondary/40 p-3 text-sm flex items-start gap-2" data-testid="invoice-locked-notice">
                  <Lock className="h-4 w-4 mt-0.5 shrink-0 text-muted-foreground" />
                  <span>Invoice sudah <b>final</b> — harga, biaya dan diskon order terkunci. {isAdmin ? "Buka kunci (dengan alasan) untuk mengubah." : "Hanya admin yang dapat membuka kunci."}</span>
                </div>
              )}
              {invoice.status === "PAID" && (
                <div className="rounded-xl border border-[hsl(var(--success)/0.3)] bg-[hsl(var(--success-bg))] p-3 text-sm flex items-start gap-2" data-testid="invoice-paid-notice">
                  <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0 text-[hsl(var(--success))]" />
                  <span>Invoice sudah <b>lunas</b>. Menambah biaya (mis. ongkir setelah ditimbang) akan membuat sisa tagihan baru — Anda akan diminta konfirmasi.</span>
                </div>
              )}

              <div className="grid sm:grid-cols-2 gap-4 text-sm">
                <div className="rounded-xl bg-secondary/40 p-3">
                  <p className="text-xs text-muted-foreground mb-1">Kepada</p>
                  <p className="font-medium">{invoice.customer.name}</p>
                  {invoice.customer.whatsapp && <p className="text-muted-foreground">{invoice.customer.whatsapp}</p>}
                  {(invoice.customer.address || invoice.customer.city) && <p className="text-muted-foreground">{[invoice.customer.address, invoice.customer.city].filter(Boolean).join(", ")}</p>}
                </div>
                <div className="rounded-xl bg-secondary/40 p-3">
                  <p className="text-xs text-muted-foreground mb-1">Trip</p>
                  <p className="font-medium">{invoice.trip.name} {invoice.trip.code ? `(${invoice.trip.code})` : ""}</p>
                  <p className="text-muted-foreground">{invoice.trip.australia_location} → {invoice.trip.indonesia_destination}</p>
                </div>
              </div>

              <div className="space-y-4">
                {invoice.orders.map((o) => (
                  <div key={o.order_id} className="rounded-xl border border-border overflow-hidden" data-testid="invoice-order-block">
                    <div className="flex items-center justify-between gap-2 px-3 py-2 bg-secondary/40 text-sm">
                      <span className="font-medium">{o.order_number} · {o.order_date}</span>
                      <div className="flex items-center gap-1.5">
                        <Badge variant="outline" className="text-[10px]">{o.payment_status}</Badge>
                        {o.status !== "DIBATALKAN" && (
                          <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => openLines(o.order_id)} data-testid="invoice-order-lines-btn">
                            <SlidersHorizontal className="h-3.5 w-3.5 mr-1" />Biaya/Diskon
                          </Button>
                        )}
                      </div>
                    </div>
                    <table className="w-full text-sm">
                      <tbody>
                        {o.items.map((it, idx) => (
                          <tr key={idx} className="border-t border-border" data-testid="invoice-item-row">
                            <td className="px-3 py-2">
                              {it.name}{it.variant ? <span className="text-muted-foreground"> ({it.variant})</span> : ""}
                              {it.is_custom && <Badge variant="outline" className="ml-2 text-[9px] border-accent text-accent-foreground bg-accent/15">Custom</Badge>}
                              {it.overridden && <Badge variant="outline" className="ml-2 text-[9px] border-[hsl(var(--warning))] text-[hsl(var(--warning))]">disesuaikan</Badge>}
                            </td>
                            <td className="px-2 py-2 text-center text-muted-foreground w-12">x{it.quantity}</td>
                            <td className="px-2 py-2 text-right text-muted-foreground w-28 hidden sm:table-cell">{formatIDR(it.final_price)}</td>
                            <td className="px-3 py-2 text-right font-medium w-28">{formatIDR(it.line_total)}</td>
                            {isAdmin && !invoice.locked && (
                              <td className="px-1 py-2 text-right w-9">
                                <Button variant="ghost" size="icon" className="h-7 w-7" data-testid="invoice-line-edit-btn"
                                  onClick={() => setLineEdit({ order_id: it.order_id, item_index: it.item_index, unit_price: String(it.final_price || 0), name: it.name, variant: it.variant, quantity: it.quantity })}>
                                  <Pencil className="h-3.5 w-3.5" />
                                </Button>
                              </td>
                            )}
                          </tr>
                        ))}
                        {o.fee_lines.map((f) => (
                          <tr key={f.id} className="border-t border-border/60 text-muted-foreground" data-testid="invoice-order-fee-line">
                            <td className="px-3 py-1.5 text-xs" colSpan={3}>+ {f.name || "Biaya"} <span className="opacity-60">· dari order {o.order_number}</span></td>
                            <td className="px-3 py-1.5 text-right text-xs font-medium">{formatIDR(f.amount)}</td>
                            {isAdmin && !invoice.locked && <td />}
                          </tr>
                        ))}
                        {o.discount_lines.map((d) => (
                          <tr key={d.id} className="border-t border-border/60 text-[hsl(var(--warning))]" data-testid="invoice-order-discount-line">
                            <td className="px-3 py-1.5 text-xs" colSpan={3}>- {discountLabel(d)} <span className="opacity-60">· dari order {o.order_number}</span></td>
                            <td className="px-3 py-1.5 text-right text-xs font-medium">-{formatIDR(d.amount)}</td>
                            {isAdmin && !invoice.locked && <td />}
                          </tr>
                        ))}
                        {(multi || o.fee_lines.length > 0 || o.discount_lines.length > 0) && (
                          <tr className="border-t border-border bg-secondary/20">
                            <td className="px-3 py-1.5 text-xs font-medium" colSpan={3}>Total {o.order_number}</td>
                            <td className="px-3 py-1.5 text-right text-sm font-semibold" data-testid="invoice-order-total">{formatIDR(o.order_total)}</td>
                            {isAdmin && !invoice.locked && <td />}
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                ))}
              </div>

              <div className="flex flex-col items-end gap-1 text-sm pt-2 border-t border-border" data-testid="invoice-totals">
                <div className="w-full sm:w-80">
                  <PricingBreakdown
                    subtotal={invoice.totals.subtotal}
                    feeLines={invoice.fee_lines}
                    discountLines={invoice.discount_lines}
                    total={invoice.totals.grand_total}
                    paid={invoice.totals.paid}
                    balance={invoice.totals.balance}
                    overpaid={invoice.totals.overpaid}
                    totalLabel="Total Tagihan"
                    testId="invoice-breakdown"
                    lineExtra={multi ? (l) => <span className="text-[10px] opacity-60"> · {l.order_number}</span> : null}
                  />
                  <div className="flex justify-between w-full text-muted-foreground mt-1"><span className="flex items-center gap-1"><Weight className="h-3.5 w-3.5" />Estimasi Berat</span><span>{invoice.totals.total_weight_kg} kg</span></div>
                </div>
                {invoice.notes && <p className="w-full text-xs text-muted-foreground mt-2 pt-2 border-t border-border" data-testid="invoice-notes-text">Catatan: {invoice.notes}</p>}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Per-order fee & discount editor — writes straight onto the order */}
      <Dialog open={linesOpen} onOpenChange={setLinesOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="invoice-lines-dialog">
          <DialogHeader><DialogTitle>Biaya &amp; Diskon</DialogTitle></DialogHeader>
          {invoice && (
            <div className="space-y-4">
              <Field label="Order" hint={multi ? "Baris biaya/diskon disimpan pada order yang dipilih (default: order terbaru)." : "Biaya/diskon disimpan langsung pada order ini."}>
                <Select value={linesOrderId} onValueChange={switchLinesOrder} disabled={!multi}>
                  <SelectTrigger data-testid="invoice-lines-order-select"><SelectValue placeholder="Pilih order" /></SelectTrigger>
                  <SelectContent>
                    {invoice.orders.filter((o) => o.status !== "DIBATALKAN").map((o) => (
                      <SelectItem key={o.order_id} value={o.order_id}>{o.order_number} · {o.order_date} · {formatIDR(o.order_total)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <PricingLinesEditor feeLines={feeLines} setFeeLines={setFeeLines} discountLines={discountLines} setDiscountLines={setDiscountLines}
                title={null} testPrefix="invoice" emptyHint="Belum ada biaya/diskon pada order ini. Tambahkan Ongkir, Biaya Lain atau Diskon." />
              {orderById[linesOrderId] && (
                <div className="rounded-lg bg-secondary/40 px-3 py-2 text-xs flex justify-between">
                  <span className="text-muted-foreground">Perkiraan total {orderById[linesOrderId].order_number}</span>
                  <span className="font-mono font-semibold" data-testid="invoice-lines-estimate">{formatIDR(estimateTotal(orderById[linesOrderId].items_subtotal, feeLines, discountLines))}</span>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setLinesOpen(false)} data-testid="invoice-lines-cancel">Batal</Button>
            <Button onClick={() => saveLines()} disabled={saving} data-testid="invoice-lines-save">{saving && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}Simpan ke Order</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Invoice notes */}
      <Dialog open={notesOpen} onOpenChange={setNotesOpen}>
        <DialogContent className="max-w-md" data-testid="invoice-notes-dialog">
          <DialogHeader><DialogTitle>Catatan Invoice</DialogTitle></DialogHeader>
          <Field label="Catatan" hint="Tampil di bagian bawah invoice & PDF.">
            <Textarea value={notesDraft} onChange={(e) => setNotesDraft(e.target.value)} placeholder="Catatan tambahan untuk invoice ini" data-testid="invoice-notes-input" />
          </Field>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNotesOpen(false)}>Batal</Button>
            <Button onClick={saveNotes} disabled={saving} data-testid="invoice-notes-save">{saving && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Line-price editor */}
      <Dialog open={!!lineEdit} onOpenChange={(o) => !o && setLineEdit(null)}>
        <DialogContent className="max-w-sm" data-testid="invoice-line-dialog">
          <DialogHeader><DialogTitle>Ubah Harga Item</DialogTitle></DialogHeader>
          {lineEdit && (
            <div className="space-y-4">
              <div className="rounded-lg bg-secondary/40 p-3 text-sm">
                <p className="font-medium">{lineEdit.name}{lineEdit.variant ? ` (${lineEdit.variant})` : ""}</p>
                <p className="text-muted-foreground text-xs mt-0.5">Qty x{lineEdit.quantity}</p>
              </div>
              <Field label="Harga Satuan (Rp)" hint="Mengubah harga akan menandai item ini sebagai disesuaikan dan tidak mengubah harga master produk.">
                <Input type="number" value={lineEdit.unit_price} onChange={(e) => setLineEdit((l) => ({ ...l, unit_price: e.target.value }))} data-testid="invoice-line-price-input" autoFocus />
              </Field>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setLineEdit(null)} data-testid="invoice-line-cancel">Batal</Button>
            <Button onClick={() => saveLine()} disabled={saving} data-testid="invoice-line-save">{saving && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change history */}
      <Dialog open={showHistory} onOpenChange={setShowHistory}>
        <DialogContent className="max-w-md max-h-[80vh] overflow-y-auto" data-testid="invoice-history-dialog">
          <DialogHeader><DialogTitle>Riwayat Perubahan</DialogTitle></DialogHeader>
          <div className="space-y-2">
            {(invoice?.history || []).length === 0 && <EmptyState title="Belum ada riwayat" subtitle="Perubahan invoice akan tercatat di sini." />}
            {(invoice?.history || []).slice().reverse().map((h) => (
              <div key={h.id} className="rounded-lg border border-border p-3 text-sm" data-testid="invoice-history-item">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-medium">{ACTION_LABEL[h.action] || h.action}{h.order_number ? <span className="text-muted-foreground font-normal"> · {h.order_number}</span> : null}</span>
                  <span className="text-xs text-muted-foreground">{formatDateTime(h.at)}</span>
                </div>
                <p className="text-xs text-muted-foreground mt-0.5">oleh {h.user}</p>
                {h.detail && <p className="text-xs mt-1">{h.detail}</p>}
                {h.reason && <p className="text-xs mt-1 italic">Alasan: {h.reason}</p>}
                {h.prev_total != null && h.new_total != null && h.prev_total !== h.new_total && (
                  <p className="text-xs mt-1">{formatIDR(h.prev_total)} &rarr; <span className="font-medium">{formatIDR(h.new_total)}</span></p>
                )}
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
